import { miscUiState } from '../../globalState'
import microsoftAuthflow from '../../microsoftAuthflow'
import { UTOPIA_MICROSOFT_AUTH_PROXY, UTOPIA_SERVER_HOST } from '../../utopia/constants'

export type MicrosoftDeviceCode = {
  user_code: string
  verification_uri: string
  expires_in: number
}

export type MicrosoftMinecraftVerifyResult =
  | { ok: true, profileName: string, profileId?: string, minecraftAccessToken: string }
  | { ok: false, reason: 'no_game' | 'cancelled' | 'proxy_unavailable' | 'auth_failed', message: string }

const NO_GAME_MESSAGE =
  'Ce compte Microsoft ne possède pas Minecraft Java. Achetez le jeu ou connectez-vous avec un compte qui le possède.'

const PROXY_UNAVAILABLE_MESSAGE =
  'Authentification Microsoft indisponible. Vérifiez votre connexion ou réessayez plus tard.'

function uniqueProxies (urls: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of urls) {
    const u = raw?.trim()
    if (!u || seen.has(u)) continue
    seen.add(u)
    out.push(u)
  }
  return out
}

export function getUtopiaMicrosoftAuthProxyCandidates (): string[] {
  const cfg = miscUiState.appConfig
  return uniqueProxies([
    cfg?.microsoftAuthProxy ?? '',
    cfg?.defaultProxy ?? '',
    UTOPIA_MICROSOFT_AUTH_PROXY,
  ])
}

function isProxyAuthError (err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return /does not support Microsoft|most likely is down|No auth endpoint|Failed to connect to auth server/i.test(msg)
}

async function tryCreateMicrosoftAuthFlow (proxyBaseUrl: string) {
  return microsoftAuthflow({
    tokenCaches: {},
    proxyBaseUrl,
    setProgressText: () => {},
    setCacheResult: () => {},
    connectingServer: UTOPIA_SERVER_HOST,
    profileOnly: true,
  })
}

function hasMinecraftJavaEntitlement (entitlements: unknown): boolean {
  const items = (entitlements as { items?: Array<{ name?: string }> })?.items
  if (!Array.isArray(items)) return false
  return items.some(item => {
    const n = item?.name ?? ''
    if (
      n === 'product_minecraft'
      || n === 'game_minecraft'
      || n === 'product_game_pass_pc'
      || n === 'game_java'
      || n === 'minecraft_java'
    ) return true
    const lower = n.toLowerCase()
    return (
      lower.includes('minecraft')
      && !lower.includes('dungeon')
      && !lower.includes('legend')
      && !lower.includes('earth')
    )
  })
}

/** Profil Java ou entitlements explicites — aligné sur server/microsoftProof.js */
function accountOwnsMinecraftJava (
  entitlements: unknown,
  profile: { name?: string, id?: string },
  hasAccessToken: boolean,
): boolean {
  if (hasMinecraftJavaEntitlement(entitlements)) return true
  return !!(profile.name?.trim() && (profile.id || hasAccessToken))
}

function profileFromNode (node: Record<string, unknown>): { name?: string, id?: string } {
  const nested = node.profile
  if (nested && typeof nested === 'object') {
    const p = nested as { name?: string, id?: string }
    if (p.name) {
      return { name: String(p.name), id: p.id ? String(p.id) : undefined }
    }
  }

  if (typeof node.username === 'string' && typeof node.profileId === 'string') {
    return { name: node.username, id: String(node.profileId) }
  }

  const selected = node.selectedProfile
  if (selected && typeof selected === 'object') {
    const p = selected as { name?: string, id?: string }
    if (p.name) return { name: String(p.name), id: p.id ? String(p.id) : undefined }
  }

  return {}
}

function extractProfile (data: unknown): { name?: string, id?: string } {
  if (!data || typeof data !== 'object') return {}

  const nodes: Record<string, unknown>[] = [data as Record<string, unknown>]
  const inner = (data as Record<string, unknown>).token
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    nodes.push(inner as Record<string, unknown>)
  }

  let name: string | undefined
  let id: string | undefined
  for (const node of nodes) {
    const p = profileFromNode(node)
    if (p.name) name = p.name
    if (p.id) id = p.id
  }
  return name || id ? { name, id } : {}
}

function findMinecraftAccessToken (data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined

  const tryNode = (node: Record<string, unknown>): string | undefined => {
    if (typeof node.token === 'string' && node.token.length > 32) return node.token
    const mca = node.mca
    if (mca && typeof mca === 'object') {
      const access = (mca as { access_token?: string }).access_token
      if (typeof access === 'string' && access.length > 32) return access
    }
    return undefined
  }

  const root = data as Record<string, unknown>
  return tryNode(root) ?? (root.token && typeof root.token === 'object'
    ? tryNode(root.token as Record<string, unknown>)
    : undefined)
}

async function fetchProfileViaMinecraftApi (
  accessToken: string,
  signal?: AbortSignal,
): Promise<{ name?: string, id?: string }> {
  const res = await fetch('https://api.minecraftservices.com/minecraft/profile', {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal: signal ?? AbortSignal.timeout(15_000),
  })
  if (!res.ok) return {}
  const data = await res.json() as { name?: string, id?: string }
  if (!data?.name) return {}
  return {
    name: String(data.name),
    id: data.id ? String(data.id) : undefined,
  }
}

function isNoMinecraftError (err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return /own minecraft|does the account own|not found|entitlement|profile data/i.test(msg)
}

export async function verifyMicrosoftOwnsMinecraft (options: {
  onDeviceCode: (data: MicrosoftDeviceCode) => void
  signal?: AbortSignal
}): Promise<MicrosoftMinecraftVerifyResult> {
  const proxies = getUtopiaMicrosoftAuthProxyCandidates()
  let authData: Awaited<ReturnType<typeof microsoftAuthflow>> | undefined
  let lastErr: unknown

  for (const proxyBaseUrl of proxies) {
    try {
      authData = await tryCreateMicrosoftAuthFlow(proxyBaseUrl)
      break
    } catch (err) {
      lastErr = err
      console.warn('[utopia] proxy Microsoft indisponible:', proxyBaseUrl, err)
    }
  }

  if (!authData) {
    console.error('[utopia] aucun proxy Microsoft:', lastErr)
    return { ok: false, reason: 'proxy_unavailable', message: PROXY_UNAVAILABLE_MESSAGE }
  }

  authData.setOnMsaCodeCallback((data: MicrosoftDeviceCode) => {
    options.onDeviceCode(data)
  })

  const abortPromise = options.signal
    ? new Promise<never>((_resolve, reject) => {
      const onAbort = () => reject(new Error('cancelled'))
      if (options.signal!.aborted) {
        onAbort()
        return
      }
      options.signal!.addEventListener('abort', onAbort, { once: true })
    })
    : null

  try {
    const tokenData = await (abortPromise
      ? Promise.race([authData.authFlow.getMinecraftJavaToken(), abortPromise])
      : authData.authFlow.getMinecraftJavaToken())

    let profile = extractProfile(tokenData)
    if (!profile.id) {
      const accessToken = findMinecraftAccessToken(tokenData)
      if (accessToken) {
        try {
          const fromApi = await fetchProfileViaMinecraftApi(accessToken, options.signal)
          profile = {
            name: profile.name ?? fromApi.name,
            id: profile.id ?? fromApi.id,
          }
        } catch (err) {
          console.warn('[utopia] fetch minecraft/profile:', err)
        }
      }
    }

    const profileName = profile.name?.trim()
    const accessToken = findMinecraftAccessToken(tokenData)

    if (!profileName) {
      return { ok: false, reason: 'no_game', message: NO_GAME_MESSAGE }
    }

    const entRoot = tokenData as { entitlements?: unknown, token?: { entitlements?: unknown } }
    const entitlements = entRoot.entitlements
      ?? (typeof entRoot.token === 'object' ? entRoot.token?.entitlements : undefined)

    if (!accountOwnsMinecraftJava(entitlements, profile, !!accessToken)) {
      return { ok: false, reason: 'no_game', message: NO_GAME_MESSAGE }
    }

    if (!entitlements || !hasMinecraftJavaEntitlement(entitlements)) {
      console.warn(
        '[utopia] entitlements non listés dans la réponse proxy, profil Java accepté:',
        profileName,
      )
    }

    if (!accessToken) {
      return { ok: false, reason: 'auth_failed', message: PROXY_UNAVAILABLE_MESSAGE }
    }

    return {
      ok: true,
      profileName,
      profileId: profile.id,
      minecraftAccessToken: accessToken,
    }
  } catch (err) {
    if (err instanceof Error && err.message === 'cancelled') {
      return { ok: false, reason: 'cancelled', message: 'Authentification annulée.' }
    }
    if (isNoMinecraftError(err)) {
      return { ok: false, reason: 'no_game', message: NO_GAME_MESSAGE }
    }
    if (isProxyAuthError(err)) {
      return { ok: false, reason: 'proxy_unavailable', message: PROXY_UNAVAILABLE_MESSAGE }
    }
    const msg = err instanceof Error ? err.message : String(err)
    return {
      ok: false,
      reason: 'auth_failed',
      message: msg || PROXY_UNAVAILABLE_MESSAGE,
    }
  }
}
